import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loading, EmptyState } from "@/components/States";
import { api, formatError } from "@/lib/api";

const COLOR = { approved: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800", retired: "bg-line text-ink-2" };

function QuestionEditor({ q, i, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...q, [k]: v });
  return (
    <div className="rounded-lg border border-line p-4">
      <div className="mb-2 flex items-center justify-between"><span className="font-mono text-xs text-ink-3">Q{i + 1}</span>
        <Button size="icon" variant="ghost" data-testid={`ai-edit-remove-${i}`} onClick={onRemove} aria-label="Remove question"><Trash2 className="h-4 w-4" /></Button></div>
      <Textarea data-testid={`ai-edit-question-${i}`} rows={2} value={q.question} onChange={(e) => set("question", e.target.value)} placeholder="Question" />
      <Textarea data-testid={`ai-edit-answer-${i}`} className="mt-2" rows={4} value={q.model_answer} onChange={(e) => set("model_answer", e.target.value)} placeholder="Model answer" />
      <Textarea data-testid={`ai-edit-keypoints-${i}`} className="mt-2" rows={3} value={q.key_points.join("\n")} onChange={(e) => set("key_points", e.target.value.split("\n"))} placeholder="Key points (one per line)" />
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <Input data-testid={`ai-edit-followup-${i}`} value={q.follow_up} onChange={(e) => set("follow_up", e.target.value)} placeholder="Likely follow-up" />
        <Input data-testid={`ai-edit-tip-${i}`} value={q.tip} onChange={(e) => set("tip", e.target.value)} placeholder="Tip" />
      </div>
    </div>
  );
}

function SetItem({ s, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [qs, setQs] = useState(s.questions);
  const save = async (body) => {
    try { await api.put(`/admin/interview-sets/${s.id}`, body); toast.success("Interview set saved"); setEdit(false); onSaved(); } catch (e) { toast.error(formatError(e)); }
  };
  const cleaned = () => qs.map((q) => ({ ...q, key_points: q.key_points.map((k) => k.trim()).filter(Boolean) }));
  return (
    <div data-testid={`ai-set-${s.id}`} className="rounded-xl border border-line bg-surface p-5">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`rounded-full px-2 py-0.5 font-medium capitalize ${COLOR[s.status]}`}>{s.status}</span>
        <span className="text-ink-3">{s.subject_name} · <span className="capitalize">{s.difficulty}</span> · {s.questions.length} questions · {new Date(s.created_at).toLocaleString()}</span>
      </div>
      <h4 className="mt-1 font-bold text-ink">{s.topic || "Mixed topics"}</h4>
      {edit ? (
        <div className="mt-3 flex flex-col gap-3">
          {qs.map((q, i) => <QuestionEditor key={i} q={q} i={i} onChange={(nq) => setQs(qs.map((x, j) => (j === i ? nq : x)))} onRemove={() => setQs(qs.filter((_, j) => j !== i))} />)}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid={`ai-add-question-${s.id}`} onClick={() => setQs([...qs, { question: "", model_answer: "", key_points: [], follow_up: "", tip: "" }])}><Plus className="mr-1 h-4 w-4" />Add question</Button>
            <Button size="sm" data-testid={`ai-save-${s.id}`} onClick={() => save({ questions: cleaned() })}>Save corrections</Button>
            <Button size="sm" data-testid={`ai-save-approve-${s.id}`} className="bg-emerald-700 hover:bg-emerald-800" onClick={() => save({ questions: cleaned(), status: "approved" })}>Save & approve</Button>
            <Button size="sm" variant="ghost" data-testid={`ai-cancel-${s.id}`} onClick={() => { setQs(s.questions); setEdit(false); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-ink-2">{s.questions.slice(0, 3).map((q, i) => <li key={i}>{q.question}</li>)}</ol>
          {s.questions.length > 3 && <p className="mt-1 text-xs text-ink-3">+{s.questions.length - 3} more</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            {s.status !== "approved" && <Button size="sm" data-testid={`ai-approve-${s.id}`} className="bg-emerald-700 hover:bg-emerald-800" onClick={() => save({ status: "approved" })}>Approve</Button>}
            {s.status !== "retired" && <Button size="sm" variant="outline" data-testid={`ai-retire-${s.id}`} onClick={() => save({ status: "retired" })}>Retire</Button>}
            <Button size="sm" variant="ghost" data-testid={`ai-edit-${s.id}`} onClick={() => setEdit(true)}>Review & edit</Button>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminInterviews() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const load = () => api.get(`/admin/interview-sets${status === "all" ? "" : `?status=${status}`}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="ai-filter-status" className="w-40 capitalize"><SelectValue /></SelectTrigger>
          <SelectContent>{["all", "pending", "approved", "retired"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
        <p className="text-sm text-ink-3">Approved sets are shown first to learners and marked "Expert-reviewed". Retired sets are no longer served.</p>
      </div>
      {!items ? <Loading /> : items.length === 0 ? <EmptyState testId="ai-empty" title="No interview sets here" /> : (
        <div className="flex flex-col gap-3">{items.map((s) => <SetItem key={`${s.id}-${s.reviewed_at || ""}`} s={s} onSaved={load} />)}</div>
      )}
    </div>
  );
}
